import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useAllMetrics } from 'features/metrics/useAllMetrics';
import { IconArrowNarrowUp, IconArrowNarrowDown } from '@tabler/icons-react';
import { getMetricValue, getMetricCardLabelByWorkBookName, getMeasureName } from './claimsTrendMetricsCardHelper';
import { MetricCardErrorState, MetricCardLoadingState, MetricCardNoDataText } from './MetricCardStates';

const ClaimsTrendMetricsCard = ({ workBookName, width, height, index }) => {
  const [metricType, setMetricType] = useState('PEPM');

  // Use unified all-metrics VDS hook (single call for all 8 tiles)
  const { data: metricsData, loading, error } = useAllMetrics();

  const handleMetricType = (event, newValue) => {
    if (newValue) setMetricType(newValue);
  };

  // Always use plain '% Diff' measure names (API no longer returns YTD/R12 variants)
  const extractedValue = getMetricValue(metricsData, getMeasureName(workBookName, metricType, null));

  // Show loading skeleton while data is being fetched
  if (loading) {
    return <MetricCardLoadingState width={width} height={height} borderRadius="26px" />;
  }

  if (error) {
    return <MetricCardErrorState title={getMetricCardLabelByWorkBookName(workBookName)} />;
  }

  if(error || !metricsData || extractedValue === null || extractedValue === undefined) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '176px',
        padding: '24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        alignSelf: 'stretch',
        borderRadius: '16px',
        border: '1px solid var(--Gray---Line-Stroke, #71767A)',
        background: 'var(--white-bi, #FFF)',
      }}
    >
      <Typography
        sx={{
          color: 'var(--Gray-Darkest---Body-Text, #121A22)',
          fontFamily: 'Noto Sans',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
        }}
      >
        {getMetricCardLabelByWorkBookName(workBookName)}
      </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', alignSelf: 'stretch' }}>
        <ToggleButtonGroup
          value={metricType}
          exclusive
          onChange={handleMetricType}
          size="small"
          sx={{
            '& .MuiToggleButton-root': { border: 'none' },
            '& .MuiToggleButton-root + .MuiToggleButton-root': { marginLeft: '12px' },
          }}
        >
          <ToggleButton
            value="PEPM"
            disableRipple
            sx={{
              px: 0,
              py: 0,
              border: 'none',
              bgcolor: 'transparent',
              fontSize: 14,
              fontWeight: 400,
              fontFamily: 'Noto Sans',
              lineHeight: '150%',
              textTransform: 'none',
              color: '#2F54A5',
              borderRadius: '8px',
              '&:active': { borderRadius: '8px' },
              '&:hover': { bgcolor: 'transparent' },
              '&:hover:not(.Mui-selected)': { color: '#068DF1' },
              '&.Mui-selected:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': {
                bgcolor: 'transparent',
                fontWeight: 600,
                textDecoration: 'underline',
                textDecorationSkipInk: 'none',
                color: '#2F54A5'
              }
            }}>
            PEPM
          </ToggleButton>
          <ToggleButton
            value="PMPM"
            disableRipple
            sx={{
              px: 0,
              py: 0,
              border: 'none',
              bgcolor: 'transparent',
              fontSize: 14,
              fontWeight: 400,
              fontFamily: 'Noto Sans',
              lineHeight: '150%',
              textTransform: 'none',
              color: '#2F54A5',
              borderRadius: '8px',
              '&:active': { borderRadius: '8px' },
              '&:hover': { bgcolor: 'transparent' },
              '&:hover:not(.Mui-selected)': { color: '#068DF1' },
              '&.Mui-selected:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': {
                bgcolor: 'transparent',
                fontWeight: 600,
                textDecoration: 'underline',
                textDecorationSkipInk: 'none',
                color: '#2F54A5'
              }
            }}>
            PMPM
          </ToggleButton>
        </ToggleButtonGroup>
        </Box>
        {extractedValue !== null ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', alignSelf: 'stretch', marginTop: 'auto' }}>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 400,
              fontStyle: 'normal',
              fontFamily: 'Noto Sans',
              color: '#121A22',
              lineHeight: '150%',
              display: 'flex',
              alignItems: 'center'
            }}>
          <>
            {parseFloat(extractedValue) > 0 ? 
              <Box
                sx={{
                  display: 'flex',
                  width: 24,
                  height: 24,
                  justifyContent: 'center',
                  alignItems: 'center',
                  aspectRatio: '1/1',
                  flexShrink: 0
                }}
              >
                <IconArrowNarrowUp stroke={2} size={24} color="#9D0038" />
              </Box>
              : <Box
                sx={{
                  display: 'flex',
                  width: 24,
                  height: 24,
                  justifyContent: 'center',
                  alignItems: 'center',
                  aspectRatio: '1/1',
                  flexShrink: 0
                }}
              >
                <IconArrowNarrowDown stroke={2} size={24} color="#007A14" />
              </Box>}
            {`${parseFloat(extractedValue).toFixed(2)}%`}
          </>
          </Typography>
        </Box>
      ) : (
        <MetricCardNoDataText />
      )}
      </Box>
  );
};

export default ClaimsTrendMetricsCard;
